import React from 'react'

import { PlotBox } from '../components/chart-segment/PlotBox'
import { ChartHeadingGap, ChartSegmentTitleSlot } from '../components/chart-segment/ChartSegmentTitleSlot'
import { clampArray } from '../foundations/slide-limits'
import { sanitizeNarrativeText } from '../foundations/text-sanitize'
import { insightBulletRhythm, schemaBackedProseClampProps } from '../foundations/text-overflow-policy'
import { registerSlide } from './registry'
import { RailLayout } from '../layouts/RailLayout'
import { presetSlot } from '../layouts/presetSlots'
import { SlotBullets, SlotHeadline, SlotNarrative } from '../slots'
import { PLOT_CARD_SURFACE } from '../slots/PlotCard'
import { SlideQuoteCard } from './SlideQuoteCard'
import { ChoiceSlideBars, RecordedChoiceSlideBars, renderCutSplit } from './choice-slide-chart'
import { EditableSlideText } from './EditableSlideText'
import type { EditableSlideProps } from './contracts'
import type { StoredChoiceSlide } from '../plots/choice-bars'
import type { MultiSelectBarsSlideData } from './renderer-internal-types'

// legacy reader only: the narrative eyebrow is required, so a deck built today stamps it.
const LEGACY_EYEBROW = 'Multi select'

const GRAY = 'var(--slide-text-muted)'
const BRAND_EMPHASIS = 'var(--slide-accent)'
const MULTI_SELECT_SLIDE_TYPE = 'multi-select-bars'
const CHART_TITLE_CLAMP = schemaBackedProseClampProps('chartTitle', MULTI_SELECT_SLIDE_TYPE, {
  lineHeight: '1.2',
})
const ACTION_SUBTITLE_CLAMP = schemaBackedProseClampProps('actionSubtitle', MULTI_SELECT_SLIDE_TYPE, {
  lineHeight: '1.5',
})

const HEADLINE = presetSlot(MULTI_SELECT_SLIDE_TYPE, 'headline').text!

function MultiSelectBarsSlideComponent(props: MultiSelectBarsSlideData & EditableSlideProps & { plot?: StoredChoiceSlide['plot']; locale?: string | null }) {
  // D17: forwarded to every clamp that offers a truncation tooltip. In a static export
  // (the headless-browser PDF pass) a Radix trigger can never open, so those clamps fall
  // back to the native `title`, which is the only carrier a printed page can hold.
  const { editable, onEdit, renderMode } = props
  const resolvedActionTitle = sanitizeNarrativeText(props.actionTitle)
  const logoUrl = props.clientLogoUrl ?? null
  const bars = clampArray(props.bars || [], 'multi-select-bars', 'bars')
  // A chart split by one computed cut draws its groups in place of the bars
  // (cuts throughout, Step 4); the gear still requests the whole question.
  const split =
    props.segmentSplit && props.segmentRows?.length
      ? { rows: clampArray(props.segmentRows, 'multi-select-bars', 'segmentRows'), split: props.segmentSplit }
      : null
  const quote = props.quoteCard
  // One or two bullets take the column's roomier rhythm (see insightBulletRhythm).
  const bulletRhythm = insightBulletRhythm(props.insightBullets?.length ?? 0)
  // Dense charts (6-10 bars) compact the row spacing/labels so all bars fit the fixed panel.
  const dense = bars.length > 5

  return (
    <RailLayout
      footerText={props.footerText}
      preset={MULTI_SELECT_SLIDE_TYPE}
      eyebrow={props.eyebrow || LEGACY_EYEBROW}
      clientLogoUrl={logoUrl}
      testIds={{ layout: 'multi-select-bars-layout', plot: 'multi-select-bars-panel', rail: 'multi-select-bars-narrative' }}
      plotStyle={{
        ...PLOT_CARD_SURFACE,
        boxSizing: 'border-box',
        // A split ships whole: content past its budget stays visible (the builder warns).
        overflow: split ? 'visible' : 'hidden',
      }}
      railStyle={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}
      plot={
          <PlotBox
            slide={props as unknown as Record<string, unknown>}
            slideType="multi-select-bars"
            columns={bars.map((bar) => ({ id: bar.id, label: bar.label }))}
            title={props.chartTitle || props.title || ''}
            showsSplit={split !== null}
            renderRecordedFigure={(source) => <RecordedChoiceSlideBars source={source} title={props.chartTitle || props.title || ''} testIdPrefix="multi-select" renderMode={renderMode} plotOtherToggleLabels={props.plotOtherToggleLabels} multiSelectNote={props.displayLabels?.multiSelectNote || 'Multiple selections allowed: percentages are independent'} />}
          >
          {split ? (
            renderCutSplit({
              slideType: 'multi-select-bars',
              chartTitle: props.chartTitle || props.title || '',
              rows: split.rows,
              split: split.split,
            })
          ) : (
          <div style={{ height: '100%', padding: '40px 32px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
          {/* The heading is the question, whole: it wraps up to the schema's line budget. */}
          <ChartSegmentTitleSlot contentPadRight={32} style={{ flexShrink: 0 }}>
          <EditableSlideText
            data-testid="multi-select-bars-chart-title"
            value={props.chartTitle || props.title || ''}
            field="chartTitle"
            editable={editable}
            onEdit={onEdit}
            tooltipOnTruncate
            renderMode={renderMode}
            minLines={CHART_TITLE_CLAMP.minLines}
            maxLines={CHART_TITLE_CLAMP.maxLines}
            lineHeight={CHART_TITLE_CLAMP.lineHeight}
            style={{ fontSize: 16, fontWeight: 600, color: 'var(--slide-text-primary)' }}
          />
          </ChartSegmentTitleSlot>
          <ChartHeadingGap height={dense ? 28 : 66} minHeight={dense ? 12 : 20} />
          <ChoiceSlideBars
            slide={{
              type: 'multi-select-bars',
              bars,
              displayLabels: (props as { displayLabels?: StoredChoiceSlide['displayLabels'] }).displayLabels,
              plot: props.plot,
            }}
            testIdPrefix="multi-select"
            locale={props.locale}
            editable={editable}
            onEdit={onEdit}
            renderMode={renderMode}
            plotOtherToggleLabels={props.plotOtherToggleLabels}
            plotOtherMembersHeading={props.plotOtherMembersHeading}
          />
          <div style={{ marginTop: 16, fontSize: 12, color: GRAY, fontStyle: 'italic', flexShrink: 0 }}>
            {/* legacy reader only: the builder stamps `displayLabels.multiSelectNote`. */}
            {props.displayLabels?.multiSelectNote || 'Multiple selections allowed: percentages are independent'}
          </div>
          </div>
          )}
          </PlotBox>
      }
    >
          {/* flexShrink: 0 is intentional: TextClamp already enforces maxHeight on both
              children, so the block height is bounded by CSS. This ensures the headline
              is never squeezed in favour of the quote card; the outer overflow: hidden
              on the section acts as the final safety net. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 36, flexShrink: 0, minWidth: 0 }}>
            <SlotHeadline text={resolvedActionTitle || ''} maxLines={HEADLINE.maxLines} editable={editable} onEdit={onEdit} />
            {props.actionSubtitle ? (
              <SlotNarrative
                value={props.actionSubtitle}
                clamp={ACTION_SUBTITLE_CLAMP}
                testId="multi-select-bars-action-subtitle"
                editable={editable}
                onEdit={onEdit}
                renderMode={renderMode}
              />
            ) : null}
            {props.insightBullets?.length ? (
              <SlotBullets
                bullets={props.insightBullets}
                fontSize={bulletRhythm.fontSize}
                gap={bulletRhythm.gap}
                maxLines={bulletRhythm.maxLines}
                dotColor={BRAND_EMPHASIS}
                testId="multi-select-bars-insight-bullets"
                editable={editable}
                onEdit={onEdit}
                renderMode={renderMode}
              />
            ) : null}
          </div>

          {quote ? (
            <SlideQuoteCard quoteCard={quote} testId="multi-select-bars-quote-card" />
          ) : null}
    </RailLayout>
  )
}

export const MultiSelectBarsSlide = registerSlide('multi-select-bars', MultiSelectBarsSlideComponent)
